import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

type Tone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: Tone;
  title: string;
  message?: string;
}

interface ToastApi {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const STYLE: Record<Tone, { icon: typeof Info; iconClass: string }> = {
  success: { icon: CheckCircle2, iconClass: 'text-green-600' },
  error: { icon: AlertCircle, iconClass: 'text-red-600' },
  info: { icon: Info, iconClass: 'text-brand-600' },
};

/**
 * Non-blocking notifications, replacing window.alert(). Errors and long
 * messages stay until dismissed (they hold detail worth reading); others fade after 5s.
 *   const toast = useToast(); toast.success('Saved'); toast.error('Sync failed', err.message);
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const push = useCallback((tone: Tone, title: string, message?: string) => {
    const id = nextId.current++;
    // Keep at most 4 on screen.
    setItems((xs) => [...xs.slice(-3), { id, tone, title, message }]);
    // Errors and long messages stay until dismissed; short ones fade.
    if (tone !== 'error' && (message?.length ?? 0) <= 160) setTimeout(() => dismiss(id), 5000);
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    success: (t, m) => push('success', t, m),
    error: (t, m) => push('error', t, m),
    info: (t, m) => push('info', t, m),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div aria-live="polite" className="pointer-events-none fixed inset-x-0 top-4 z-[70] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end">
          {items.map((t) => {
            const { icon: Icon, iconClass } = STYLE[t.tone];
            return (
              <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'}
                className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-slate-200 bg-surface px-4 py-3 shadow-xl ring-1 ring-slate-900/5 dark:ring-white/10 animate-[popIn_160ms_ease-out]">
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{t.title}</p>
                  {t.message && <p className="mt-0.5 whitespace-pre-line break-words text-sm text-slate-500">{t.message}</p>}
                </div>
                <button onClick={() => dismiss(t.id)} className="-mr-1 shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600" aria-label="Dismiss">
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}
